import type { Basic, Practitioner, PractitionerRole } from "@medplum/fhirtypes";

import type {
  ClinicPractitioner,
  ClinicPractitionerSearch,
  PractitionerRemoval,
  PractitionerSource,
} from "@/types/clinic-configuration";
import type { MemberRole } from "@/types/users";
import {
  FHIR_CODES,
  FHIR_EXTENSIONS,
  FHIR_IDENTIFIER_SYSTEMS,
} from "@/constants/fhir.constants";
import {
  memberPractitionerSchema,
  practitionerSchema,
  schedulingPractitionerSchema,
} from "@/lib/schemas";
import { parseMemberRole } from "@/lib/user-access";
import { formatHumanName, getExtensionValue } from "@/lib/utils/fhir";
import { getIdentifierValues, getReferenceId } from "@/lib/utils/helpers/fhir";
import { isString } from "@/lib/utils/typeof";

import { filterBySearchText } from "./search-text";

/**
 * The name without its title ("Dr", "Dra"). `Avatar` builds initials from
 * the words it gets, and the title would give every practitioner the same first
 * letter.
 */
export const getPractitionerNameWithoutTitle = (
  practitioner: Practitioner
): string => {
  const name = practitioner.name?.[0];

  return [...(name?.given ?? []), name?.family]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(" ");
};

/**
 * Each practitioner's distinct specialties, by `Practitioner` id. AKB writes one
 * role per practitioner and location and copies the same specialty onto each, so
 * this is one value in practice. Every `specialty` of a role is read, not
 * only the first. Roles that disagree show every value
 * rather than one picked at random. A role with no practitioner (the
 * clinic's patient-coordinator role) belongs to no practitioner and is skipped.
 */
export const groupPractitionerSpecialties = (
  roles: PractitionerRole[]
): Record<string, string[]> => {
  const specialties: Record<string, string[]> = {};

  for (const role of roles) {
    const practitionerId = getReferenceId(
      role.practitioner?.reference,
      "Practitioner"
    );
    const roleSpecialties = (role.specialty ?? [])
      .map(({ text }) => text?.trim())
      .filter((text): text is string => isString(text) && text !== "");

    if (!practitionerId) {
      continue;
    }

    for (const specialty of roleSpecialties) {
      const current = specialties[practitionerId] ?? [];
      if (!current.includes(specialty)) {
        specialties[practitionerId] = [...current, specialty];
      }
    }
  }

  return specialties;
};

/**
 * The practitioners whose name or specialty contains `query`. An empty query
 * keeps them all.
 */
export const filterPractitioners = (
  practitioners: ClinicPractitioner[],
  specialties: Record<string, string[]>,
  query: string
): ClinicPractitioner[] =>
  filterBySearchText(practitioners, query, ({ practitioner }) => [
    formatHumanName(practitioner),
    ...(specialties[practitioner.id ?? ""] ?? []),
  ]);

/**
 * `_revinclude=Basic:subject` returns every `Basic` whose `subject` points at
 * a practitioner, so the member code is checked here, not by the search.
 * `code?.`: FHIR requires `code`, but any system can write a `Basic`, and
 * one without it must not break the whole Practitioners list and its count.
 */
const isMemberOf = (basic: Basic, practitionerId: string | undefined) =>
  !!practitionerId &&
  basic.code?.coding?.some(({ code }) => code === FHIR_CODES.MEMBER) === true &&
  getReferenceId(basic.subject?.reference, "Practitioner") === practitionerId;

/**
 * The Practitioners page's practitioners: the ones with at least one source, in
 * the order the server returned them. The rest (Huli, a removed team member,
 * a test record) are not the clinic's practitioners. FHIR search combines
 * different parameters with AND, so "one source or another" cannot be the
 * search itself. The list, the count and one record all go through this, so
 * they never disagree on who is listed.
 */
export const toClinicPractitioners = ({
  practitioners,
  members,
}: ClinicPractitionerSearch): ClinicPractitioner[] =>
  practitioners.flatMap((practitioner) => {
    const member = members.find((basic) => isMemberOf(basic, practitioner.id));
    const systems = new Set(
      practitioner.identifier?.map(({ system }) => system)
    );
    const sources: PractitionerSource[] = [];

    if (systems.has(FHIR_IDENTIFIER_SYSTEMS.PRACTITIONER)) {
      sources.push("akb");
    }
    if (systems.has(FHIR_IDENTIFIER_SYSTEMS.KNOWLEDGE_BASE_ID)) {
      sources.push("scheduling");
    }
    if (member) {
      sources.push("member");
    }

    return sources.length > 0 ? [{ practitioner, sources, member }] : [];
  });

/**
 * The schema the Practitioner drawer edits this practitioner with, and
 * `updatePractitioner` patches it with, so they never disagree. The email
 * is editable only on an AKB-only one: a Scheduling one's shows on the
 * Calendars screen, and a member's is its login. Either wins over AKB, and
 * member wins over Scheduling.
 */
export const getClinicPractitionerSchema = ({
  sources,
}: ClinicPractitioner) => {
  if (sources.includes("member")) {
    return memberPractitionerSchema;
  }
  if (sources.includes("scheduling")) {
    return schedulingPractitionerSchema;
  }
  return practitionerSchema;
};

/**
 * How the Practitioner drawer takes this practitioner off the clinic, and what
 * `deactivatePractitioner` checks again on a fresh read, so they never
 * disagree. Only an AKB-only practitioner is deactivated here. A member's login
 * is removed on the Team page, which leaves the `Practitioner` as it is.
 * A Scheduling practitioner, or an AKB one with another source, gets
 * nothing: deactivating them is not designed yet.
 */
export const getPractitionerRemoval = ({
  sources,
}: ClinicPractitioner): PractitionerRemoval | undefined => {
  if (sources.includes("member")) {
    return "teamPage";
  }
  if (sources.length === 1 && sources[0] === "akb") {
    return "deactivate";
  }
  return undefined;
};

/**
 * The team role of a member practitioner. `undefined` for a practitioner
 * that is not a member, or a member whose role is not one the app knows.
 */
export const getClinicPractitionerRole = ({
  member,
}: ClinicPractitioner): MemberRole | undefined =>
  parseMemberRole(
    getExtensionValue<string>(member, FHIR_EXTENSIONS.MEMBER_ROLE)
  );

/**
 * The ID the drawer header shows: AKB's, else Agent Scheduling's. A member
 * shows none, even with one of them: giving members a Practitioner ID is left
 * to the duplicate merge work.
 */
export const getPractitionerCode = ({
  practitioner,
  sources,
}: ClinicPractitioner): string | undefined => {
  if (sources.includes("member")) {
    return undefined;
  }

  const [akbId] = getIdentifierValues(
    [practitioner],
    FHIR_IDENTIFIER_SYSTEMS.PRACTITIONER
  );
  const [schedulingId] = getIdentifierValues(
    [practitioner],
    FHIR_IDENTIFIER_SYSTEMS.KNOWLEDGE_BASE_ID
  );

  return akbId ?? schedulingId;
};
